import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { supabase } from "@/integrations/supabase/client";

export interface ApiError {
  error: string;
  status?: number;
  details?: unknown;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8000";

    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add authentication
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          // Get the session
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Error getting session:', error);
          }

          if (session?.access_token) {
            // Add the Supabase JWT token
            config.headers.Authorization = `Bearer ${session.access_token}`;
            // Also send the user ID for backend reference
            config.headers["X-User-Id"] = session.user.id;
            config.headers["X-User-Email"] = session.user.email || "";
          } else {
            // For anonymous users, we can set a special header
            config.headers["X-Anonymous-User"] = "true";
          }
        } catch (error) {
          // Continue without auth headers if there's an error
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        if (error.response) {
          // Handle specific error cases
          if (error.response.status === 401) {
            // Token might be expired, try to refresh
            const {
              data: { session },
              error: refreshError,
            } = await supabase.auth.refreshSession();

            if (!refreshError && session && error.config) {
              // Retry the original request with new token
              error.config.headers.Authorization = `Bearer ${session.access_token}`;
              error.config.headers["X-User-Id"] = session.user.id;
              error.config.headers["X-User-Email"] = session.user.email || "";
              return this.client.request(error.config);
            }
          }

          // Transform error to consistent format
          const apiError: ApiError = {
            error:
              error.response.data?.error ||
              error.message ||
              "An unexpected error occurred",
            status: error.response.status,
            details: error.response.data,
          };

          return Promise.reject(apiError);
        } else if (error.request) {
          // Network error
          const apiError: ApiError = {
            error: "Network error. Please check your connection.",
            status: 0,
          };
          return Promise.reject(apiError);
        } else {
          // Something else went wrong
          const apiError: ApiError = {
            error: error.message || "An unexpected error occurred",
          };
          return Promise.reject(apiError);
        }
      }
    );
  }

  // Generic request methods
  async get<T>(url: string, configOrParams?: AxiosRequestConfig | any): Promise<T> {
    // If configOrParams looks like query params (plain object without axios config properties)
    if (configOrParams && !('headers' in configOrParams) && !('timeout' in configOrParams) && !('baseURL' in configOrParams)) {
      const response = await this.client.get<T>(url, { params: configOrParams });
      return response.data;
    }
    // Otherwise treat it as axios config
    const response = await this.client.get<T>(url, configOrParams);
    return response.data;
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();
